import { useState } from "react";
import { Play, Pause, Clock, ArrowUpRight } from "lucide-react";
import { Reveal, SectionHead } from "@/components/Reveal";
import { SpotifyIcon } from "@/components/SocialIcons";
import { MIXES } from "@/content";

const Waveform = ({ playing }) => (
  <div className="flex items-end gap-[2px] h-8 flex-1" aria-hidden="true">
    {Array.from({ length: 42 }).map((_, i) => (
      <span
        key={i}
        className={`wave-bar ${playing ? "playing" : ""}`}
        style={{
          height: `${22 + ((i * 37) % 78)}%`,
          animationDelay: playing ? `${(i % 10) * 0.09}s` : undefined,
          animationDuration: playing ? `${0.7 + (i % 4) * 0.15}s` : undefined,
        }}
      />
    ))}
  </div>
);

const MixCard = ({ mix, index }) => {
  const [playing, setPlaying] = useState(false);
  const embed = `https://w.soundcloud.com/player/?url=${encodeURIComponent(
    mix.scUrl
  )}&color=%23ccff00&auto_play=true&hide_related=true&show_comments=false&show_reposts=false&visual=false`;

  return (
    <Reveal delay={index * 0.12} className="glass rounded-2xl overflow-hidden hover:border-cy/40 hover:shadow-[0_0_35px_rgba(0,240,255,0.12)] transition-all">
      <div className="relative aspect-square overflow-hidden">
        {playing ? (
          <iframe
            title={`Play ${mix.title} on SoundCloud`}
            src={embed}
            data-testid={`mix-embed-${mix.id}`}
            className="w-full h-full border-0"
            allow="autoplay"
            loading="lazy"
          />
        ) : (
          <>
            <img src={mix.artwork} alt={`${mix.title} mix artwork`} loading="lazy" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-ink/90 via-ink/20 to-transparent" />
          </>
        )}
        <span className="absolute top-4 left-4 font-mono text-[10px] tracking-[0.25em] uppercase bg-ink/70 backdrop-blur px-3 py-1.5 rounded-full text-volt border border-volt/30">
          {mix.genre}
        </span>
      </div>
      <div className="p-5">
        <h3 className="font-head font-extrabold uppercase tracking-tight text-lg leading-tight">{mix.title}</h3>
        <div className="mt-4 flex items-center gap-4">
          <button
            data-testid={`mix-play-${mix.id}`}
            onClick={() => setPlaying(!playing)}
            aria-label={playing ? `Pause ${mix.title}` : `Play ${mix.title}`}
            className={`shrink-0 w-12 h-12 rounded-full flex items-center justify-center transition-all ${
              playing
                ? "bg-volt text-ink shadow-[0_0_25px_rgba(204,255,0,0.45)]"
                : "border border-white/20 text-white hover:border-volt hover:text-volt"
            }`}
          >
            {playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
          </button>
          <Waveform playing={playing} />
        </div>
        <p className="mt-4 flex items-center gap-2 font-mono text-[11px] tracking-[0.2em] uppercase text-zinc-500">
          <Clock className="w-3.5 h-3.5" /> {mix.duration} - SoundCloud
        </p>
      </div>
    </Reveal>
  );
};

export default function Music() {
  return (
    <section id="music" data-testid="music-section" className="relative py-20 lg:py-32 bg-card2/40">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(204,255,0,0.05),transparent_55%)]" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHead
          eyebrow="Music & Mixes"
          title={<>Press Play. <span className="text-neon">Turn Up.</span></>}
          sub="Fresh mixes and live audio straight from the decks — streamed directly from QT 2hype's SoundCloud."
        />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {MIXES.map((m, i) => (
            <MixCard key={m.id} mix={m} index={i} />
          ))}
        </div>

        <div className="mt-12 grid md:grid-cols-2 gap-6">
          <Reveal>
            <div className="glass rounded-2xl overflow-hidden hover:border-[#1DB954]/40 hover:shadow-[0_0_40px_rgba(29,185,84,0.12)] transition-all h-full">
              <div className="flex items-center justify-between gap-4 px-6 pt-5 pb-4">
                <div className="flex items-center gap-3">
                  <span className="w-10 h-10 rounded-full bg-[#1DB954]/15 text-[#1DB954] flex items-center justify-center">
                    <SpotifyIcon className="w-5 h-5" />
                  </span>
                  <div>
                    <p className="font-head font-extrabold uppercase tracking-tight">QT 2hype On Spotify</p>
                    <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-zinc-500 mt-0.5">Stream the edits & road mixes</p>
                  </div>
                </div>
                <a
                  href="https://open.spotify.com/artist/1vVMPFff7sBi1Ww4qHszS8"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="music-spotify-link"
                  className="shrink-0 inline-flex items-center gap-1.5 font-head font-bold uppercase text-xs tracking-wider text-[#1DB954] hover:underline"
                >
                  Follow <ArrowUpRight className="w-4 h-4" />
                </a>
              </div>
              <div className="bg-ink">
                <iframe
                  data-testid="music-spotify-embed"
                  title="QT 2hype on Spotify"
                  src="https://open.spotify.com/embed/artist/1vVMPFff7sBi1Ww4qHszS8?utm_source=generator&theme=0"
                  className="w-full border-0"
                  height="352"
                  loading="lazy"
                  allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                />
              </div>
              <p className="px-6 py-3.5 font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-600">
                Free 30-second previews without an account - full tracks open in Spotify
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="glass rounded-2xl overflow-hidden hover:border-[#1DB954]/40 hover:shadow-[0_0_40px_rgba(29,185,84,0.12)] transition-all h-full">
              <div className="flex items-center justify-between gap-4 px-6 pt-5 pb-4">
                <div className="flex items-center gap-3">
                  <span className="w-10 h-10 rounded-full bg-[#1DB954]/15 text-[#1DB954] flex items-center justify-center">
                    <SpotifyIcon className="w-5 h-5" />
                  </span>
                  <div>
                    <p className="font-head font-extrabold uppercase tracking-tight">Wuk Up Again Riddim</p>
                    <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-zinc-500 mt-0.5">The full riddim playlist</p>
                  </div>
                </div>
                <a
                  href="https://open.spotify.com/playlist/6lgCzv8vopllHBdwWEJ5PY"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="music-wukup-link"
                  className="shrink-0 inline-flex items-center gap-1.5 font-head font-bold uppercase text-xs tracking-wider text-[#1DB954] hover:underline"
                >
                  Open <ArrowUpRight className="w-4 h-4" />
                </a>
              </div>
              <div className="bg-ink">
                <iframe
                  data-testid="music-wukup-embed"
                  title="Wuk Up Again Riddim playlist on Spotify"
                  src="https://open.spotify.com/embed/playlist/6lgCzv8vopllHBdwWEJ5PY?utm_source=generator&theme=0"
                  className="w-full border-0"
                  height="352"
                  loading="lazy"
                  allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                />
              </div>
              <p className="px-6 py-3.5 font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-600">
                Free 30-second previews without an account - full tracks open in Spotify
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
